import React, {
  createContext,
  MouseEvent,
  PropsWithChildren,
  TouchEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type IDLike = string | number
type WithId = any & { id: IDLike }

export interface SelectionContextType<T = WithId> {
  isEditing: boolean
  isSorting: boolean
  selectedItems: T[]
  items: T[]
  isSelected: (obj: T) => boolean
  actions: {
    setItems: (objs: T[]) => void
    startEditing: () => void
    stopEditing: () => void
    selectAll: () => void
    setSorting: (sorting: boolean) => void
  }
  events: {
    onMouseDown: (obj: T, event: MouseEvent<HTMLElement>) => boolean
    onMouseUp: (obj: T, event: MouseEvent<HTMLElement>) => boolean
    onTouchStart: (obj: T, event: TouchEvent<HTMLElement>) => boolean
    onTouchEnd: (obj: T, event: TouchEvent<HTMLElement>) => boolean
  }
}

export const SelectionContext = createContext<SelectionContextType>({
  isEditing: false,
  isSorting: false,
  isSelected: () => false,
  items: [],
  selectedItems: [],
  actions: {
    setItems: () => null,
    startEditing: () => null,
    stopEditing: () => null,
    selectAll: () => null,
    setSorting: () => null,
  },
  events: {
    onMouseDown: () => false,
    onMouseUp: () => false,
    onTouchStart: () => false,
    onTouchEnd: () => false,
  },
})

interface Props {
  initialItems?: WithId[]
}

const SelectionProvider: React.FC<PropsWithChildren<Props>> = ({ initialItems, children }) => {
  const [items, setItems] = useState<WithId[]>(initialItems ?? [])
  const [justSelected, setJustSelected] = useState(false)
  const [selectedItems, setSelectedItems] = useState(new Set<IDLike>())
  const [isEditing, setIsEditing] = useState(false)
  const [isSorting, setSorting] = useState(false)
  const [lastSelectedItem, setLastSelectedItem] = useState<IDLike | null>(null)

  // If we get an update to the initial items list, update the local state
  useEffect(() => {
    setItems(initialItems ?? [])
  }, [initialItems])

  /** Select only the given item */
  const selectOnly = useCallback((id: IDLike) => {
    setLastSelectedItem(id)
    setSelectedItems(new Set([id]))
  }, [])

  /** Select no items */
  const selectNone = useCallback(() => {
    setLastSelectedItem(null)
    setSelectedItems(new Set())
  }, [])

  /** Select all items */
  const selectAll = useCallback(() => {
    setLastSelectedItem(items[0].id)
    setSelectedItems(new Set(items))
  }, [items])

  /** Extend the selection to a given item */
  const extendSelection = useCallback(
    (id: IDLike) => {
      if (!lastSelectedItem) return

      const lastSelectionIdx = items.findIndex((obj) => obj.id === lastSelectedItem)
      const itemIdx = items.findIndex((obj) => obj.id === id)
      if (lastSelectionIdx >= 0 && itemIdx >= 0) {
        const minIdx = Math.min(lastSelectionIdx, itemIdx)
        const maxIdx = Math.max(lastSelectionIdx, itemIdx)
        for (let i = 0; i < items.length; i++) {
          if (i >= minIdx && i <= maxIdx) {
            selectedItems.add(items[i].id)
          } else {
            selectedItems.delete(items[i].id)
          }
        }
      }
      setSelectedItems(new Set(selectedItems))
    },
    [lastSelectedItem, items, selectedItems]
  )

  /** Toggle the selection of an item */
  const toggleSelection = useCallback(
    (id: IDLike) => {
      if (selectedItems.has(id)) {
        selectedItems.delete(id)
        setLastSelectedItem(null)
      } else {
        selectedItems.add(id)
        setLastSelectedItem(id)
      }
      setSelectedItems(new Set(selectedItems))
    },
    [selectedItems]
  )

  const onMouseDown = useCallback(
    (obj: WithId, event: MouseEvent<HTMLElement>) => {
      if (event.shiftKey) {
        // If we have a shift-click, extend the selection prior to drag
        extendSelection(obj.id)
        setJustSelected(true)
      } else if (selectedItems.has(obj.id)) {
        // If item was already selected, take note of that
        setJustSelected(false)
      } else {
        if (isEditing) {
          toggleSelection(obj.id)
          setJustSelected(true)
        } else {
          // If item isn't selected yet, select it by itself
          selectOnly(obj.id)
          setJustSelected(true)
        }
      }
      return true
    },
    [isEditing, extendSelection, toggleSelection, selectOnly, selectedItems]
  )

  const onMouseUp = useCallback(
    (obj: WithId) => {
      // If this item was not freshly selected on mouse down, then toggle it off on mouse up
      if (!justSelected) {
        if (isEditing) {
          toggleSelection(obj.id)
        } else {
          selectNone()
        }
      }
      return true
    },
    [justSelected, isEditing, toggleSelection, selectNone]
  )

  const contextValue: SelectionContextType = {
    selectedItems: useMemo(
      () =>
        Array.from(selectedItems)
          .map((id) => items.find((item) => item.id === id))
          .filter((item) => !!item),
      [selectedItems, items]
    ),
    items,
    isEditing,
    isSorting,
    isSelected: useCallback((obj) => selectedItems.has(obj.id), [selectedItems]),
    events: {
      onMouseDown,
      onMouseUp,
      onTouchStart: useCallback(
        (obj, event) => {
          return onMouseDown(obj, event as any)
        },
        [onMouseDown]
      ),
      onTouchEnd: onMouseUp,
    },
    actions: {
      startEditing: useCallback(() => setIsEditing(true), []),
      stopEditing: useCallback(() => {
        setIsEditing(false)
        selectNone()
        setSorting(false)
      }, [selectNone]),
      selectAll,
      setItems,
      setSorting,
    },
  }
  return <SelectionContext.Provider value={contextValue}>{children}</SelectionContext.Provider>
}
export default SelectionProvider

export function useSelection<T = any>(): SelectionContextType<T> {
  return useContext(SelectionContext)
}
